import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltTile } from '@cae/cobalt-react/components/tile';
import { CobaltIconArrowRightOutline } from '@cae/cobalt-react/icons/outline/CobaltIconArrowRightOutline';
import { getFullName } from '@shared/helpers/get-full-name/getFullName';
import * as React from 'react';

import type { NameDiscrepancyContentProps } from './NameDiscrepancyContent.types';

import { DiscrepancyIllustration } from '../illustrations/DiscrepancyIllustration';
import sharedStyles from '../shared/DiscrepancyLayout.module.css';

const NameDiscrepancyContent = React.memo(
  ({
    sfContact,
    caeConnectContact,
    isSyncing,
    onSync,
    onGoHome,
  }: Readonly<NameDiscrepancyContentProps>): React.ReactElement => {
    const sfName = getFullName(
      sfContact.firstName,
      sfContact.middleName,
      sfContact.lastName
    );
    const connectName = getFullName(
      caeConnectContact.firstName,
      caeConnectContact.middleName,
      caeConnectContact.lastName
    );

    return (
      <div
        className={sharedStyles.discrepancyPage}
        data-testid="name-discrepancy-content"
      >
        <DiscrepancyIllustration />

        <section
          className={sharedStyles.contentSection}
          aria-labelledby="name-discrepancy-title"
        >
          <h1 className="heading-02" id="name-discrepancy-title">
            It looks like the contact you selected already exists in CAE
            Connect, but there are discrepancies in the information.
          </h1>

          <div className={sharedStyles.comparisonContainer}>
            <CobaltTile className={sharedStyles.comparisonBox}>
              <p className={`body-01 ${sharedStyles.comparisonLabel}`}>
                Salesforce data
              </p>
              <p className="body-02">{sfName}</p>
              <p className="body-02">{sfContact.email}</p>
            </CobaltTile>

            <span className={sharedStyles.icon}>
              <CobaltIconArrowRightOutline size="medium" />
            </span>

            <CobaltTile className={sharedStyles.comparisonBox}>
              <p className={`body-01 ${sharedStyles.comparisonLabel}`}>
                CAE Connect data
              </p>
              <p className="body-02">{connectName}</p>
              <p className="body-02">{caeConnectContact.email}</p>
            </CobaltTile>
          </div>

          <div className={sharedStyles.contentFooter}>
            <p className="body-02">
              To resolve this, you can update the CAE Connect name to match the
              Salesforce contact information.
            </p>
          </div>
        </section>

        <div className={sharedStyles.actions}>
          <CobaltButton
            type="button"
            variant="primary"
            pending={isSyncing}
            onClick={onSync}
          >
            Update CAE Connect
          </CobaltButton>
          <CobaltButton type="button" variant="ghost" onClick={onGoHome}>
            Go to Homepage
          </CobaltButton>
        </div>
      </div>
    );
  }
);

NameDiscrepancyContent.displayName = 'NameDiscrepancyContent';

export { NameDiscrepancyContent };
